import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router';
import { StatusChip, ProgressBar, EmptyState } from '@lms/ui';
import {
  Award,
  Loader2,
  ArrowRight,
  BookOpen,
  ScrollText,
  CheckCircle2,
  TrendingUp,
  Lock,
} from 'lucide-react';
import { useEnrollments } from '../hooks/useEnrollments';
import { useEvaluations } from '../hooks/useEvaluations';
import { useAuthStore } from '../store/auth';
import { useLanguage } from '../lib/LanguageContext';
import { resolveMediaUrl } from '@lms/shared/utils';
import { Bilingual } from '../components/Bilingual';
import { copy } from '../copy/copy';
import { apiBaseUrl } from '../lib/api-client';
import { ParticipantNipBadge } from '../components/ParticipantNipBadge';
import { useParticipantNip } from '../hooks/useParticipantNip';

export function Results() {
  const { programId } = useParams<{ programId: string }>();
  const { user } = useAuthStore();
  const participantNip = useParticipantNip();
  const { data: allEnrollments, isLoading, error } = useEnrollments({ userId: user?.id });
  const {
    data: evaluationAccessData,
    isLoading: isEvaluationAccessLoading,
    error: evaluationAccessError,
  } = useEvaluations({ userId: user?.id });
  const { t } = useLanguage();

  const [selectedProgramId, setSelectedProgramId] = useState<string>(programId || '');

  // Filter enrollments based on selected program or route param
  const enrollments = useMemo(() => allEnrollments || [], [allEnrollments]);

  const activeProgramId = useMemo(() => {
    if (selectedProgramId && enrollments.some(e => e.programId === selectedProgramId)) {
      return selectedProgramId;
    }
    if (programId && enrollments.some(e => e.programId === programId)) {
      return programId;
    }
    return enrollments[0]?.programId || '';
  }, [selectedProgramId, programId, enrollments]);

  const activeEnrollment = useMemo(() => {
    return enrollments.find(e => e.programId === activeProgramId) || enrollments[0];
  }, [enrollments, activeProgramId]);

  const evaluationAccessById = useMemo(
    () => new Map((evaluationAccessData || []).map(evaluation => [evaluation.id, evaluation])),
    [evaluationAccessData]
  );

  if (isLoading || isEvaluationAccessLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[450px] space-y-4">
        <Loader2 className="w-9 h-9 text-primary animate-spin" />
        <p className="text-sm text-on-surface-variant font-body font-medium">
          {t(copy.results.calculating)}
        </p>
      </div>
    );
  }

  if (error || evaluationAccessError || !allEnrollments) {
    return (
      <div className="bg-error-container text-on-error-container p-6 rounded-2xl animate-fadeIn">
        <h3 className="font-headline font-bold mb-2">{t(copy.results.errorTitle)}</h3>
        <p className="text-sm font-body">
          {(error as Error)?.message ||
            (evaluationAccessError as Error)?.message ||
            t(copy.common.error)}
        </p>
      </div>
    );
  }

  if (enrollments.length === 0) {
    return (
      <EmptyState
        icon={Award}
        title={t(copy.results.noResultsTitle)}
        description={t(copy.results.noResultsDesc)}
      />
    );
  }

  const completedCount = enrollments.filter(r => r.status === 'COMPLETED').length;
  const avgCompletion = Math.round(
    enrollments.reduce((sum, r) => sum + r.progress, 0) / enrollments.length
  );

  return (
    <div className="space-y-8 animate-fadeIn pb-16">
      {/* Header Banner with Direct Link to Transcripts */}
      <div className="relative overflow-hidden bg-surface-container-lowest rounded-3xl p-6 sm:p-8 border border-outline-variant/10 shadow-level1 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="space-y-2 z-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-[10px] font-black uppercase tracking-widest">
            <TrendingUp size={14} />
            <span>Laporan Kemajuan Belajar Berjalan</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-headline font-black text-on-surface tracking-tight">
            {t(copy.results.title)}
          </h1>
          <p className="text-on-surface-variant text-sm sm:text-base leading-relaxed">
            {t(copy.results.subtitle)}
          </p>
          {participantNip && <ParticipantNipBadge nip={participantNip} className="mt-3" />}
        </div>

        <div className="flex flex-wrap items-center gap-3 z-10 w-full md:w-auto">
          <Link
            to="/transcripts"
            className="px-5 py-3 rounded-xl bg-primary text-on-primary font-label font-bold text-xs uppercase tracking-wider flex items-center gap-2 shadow-level1 hover:bg-primary-container active:scale-95 transition-all"
          >
            <ScrollText size={16} />
            <span>
              <Bilingual {...copy.transcripts.viewOfficialTranscript} />
            </span>
          </Link>
        </div>
      </div>

      {/* Program Selector Tabs if Multiple Programs */}
      {enrollments.length > 1 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
          {enrollments.map(e => {
            const isSelected = e.programId === activeProgramId;
            return (
              <button
                key={e.id}
                onClick={() => setSelectedProgramId(e.programId)}
                className={`px-4 py-2.5 rounded-xl font-headline font-bold text-xs whitespace-nowrap transition-all flex items-center gap-2 ${
                  isSelected
                    ? 'bg-primary text-on-primary shadow-level1'
                    : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high'
                }`}
              >
                <BookOpen size={14} />
                <span>{e.program?.name || 'Program'}</span>
                <span className="font-mono text-[10px] opacity-80">
                  ({Math.round(e.progress)}%)
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Active Program Deep-Dive Progress Card */}
      {activeEnrollment &&
        (() => {
          const enrollment = activeEnrollment;
          const isTuntas = enrollment.status === 'COMPLETED';
          const program = enrollment.program;

          // Build list of all evaluations in program
          const programEvaluations: Array<{
            id: string;
            title: string;
            type: 'STAGE' | 'LEVEL' | 'PROGRAM';
            passingScore: number;
            contextLabel: string;
          }> = [];

          program?.levels?.forEach((lvl, lvlIdx) => {
            lvl.stages?.forEach(stg => {
              stg.evaluations?.forEach(ev => {
                programEvaluations.push({
                  id: ev.id,
                  title: ev.title,
                  type: 'STAGE',
                  passingScore: ev.passingScore ?? stg.passingScore ?? 70,
                  contextLabel: `Tahap ${stg.stageCode || ''} • ${stg.name}`,
                });
              });
            });

            lvl.evaluations?.forEach(ev => {
              programEvaluations.push({
                id: ev.id,
                title: ev.title,
                type: 'LEVEL',
                passingScore: ev.passingScore ?? 70,
                contextLabel: `Level ${lvlIdx + 1} • ${lvl.name}`,
              });
            });
          });

          program?.evaluations?.forEach(ev => {
            programEvaluations.push({
              id: ev.id,
              title: ev.title,
              type: 'PROGRAM',
              passingScore: ev.passingScore ?? 70,
              contextLabel: 'Evaluasi Akhir Program',
            });
          });

          const totalEvals = programEvaluations.length;
          const legacyQualifiedIds = new Set(
            enrollment.priorLearningRecognition?.legacyQualifiedEvaluationIds ?? []
          );
          let completedEvals = 0;
          let passedEvals = 0;

          programEvaluations.forEach(ev => {
            const res = enrollment.results?.find(r => {
              const meta = r.metadata as Record<string, unknown> | undefined;
              const rUnknown = r as unknown as Record<string, unknown>;
              const resultEvalId =
                (meta?.evaluationId as string | undefined) ||
                (rUnknown.evaluationId as string | undefined);
              return resultEvalId === ev.id;
            });
            if (res) {
              completedEvals++;
              const meta = res.metadata as Record<string, unknown> | undefined;
              const rUnknown = res as unknown as Record<string, unknown>;
              const evalPassed =
                meta?.isPassed !== undefined ? Boolean(meta.isPassed) : Boolean(rUnknown.isPassed);
              if (evalPassed) passedEvals++;
            } else if (legacyQualifiedIds.has(ev.id)) {
              completedEvals++;
              passedEvals++;
            }
          });

          const evalPercent = totalEvals > 0 ? Math.round((completedEvals / totalEvals) * 100) : 0;
          const passPercent = totalEvals > 0 ? Math.round((passedEvals / totalEvals) * 100) : 0;
          const reviewableResults = (enrollment.results || []).flatMap(result => {
            if (result.type !== 'QUIZ_SCORE' && result.type !== 'EXAM_SCORE') return [];
            const metadata = result.metadata as Record<string, unknown> | undefined;
            const evaluationId = metadata?.evaluationId;
            const attemptId = metadata?.attemptId;
            return [
              {
                id: result.id,
                evaluationId: typeof evaluationId === 'string' ? evaluationId : null,
                attemptId: typeof attemptId === 'string' ? attemptId : null,
                title: result.title,
                score: result.score,
              },
            ];
          });

          return (
            <div className="bg-surface-container-lowest rounded-3xl shadow-level2 p-6 sm:p-8 border border-outline-variant/10 space-y-8">
              {/* Top Program Identity & Status */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-6 border-b border-outline-variant/10">
                <div className="flex items-center gap-4">
                  {program?.coverImage ? (
                    <img
                      src={resolveMediaUrl(program.coverImage, apiBaseUrl)}
                      alt={program.name}
                      className="w-16 h-12 rounded-xl object-cover shadow-xs border border-on-surface/5 shrink-0"
                    />
                  ) : null}
                  <div className="space-y-1">
                    <span className="text-[10px] font-label font-bold text-secondary uppercase tracking-widest block">
                      Program Terpilih
                    </span>
                    <h2 className="text-xl sm:text-2xl font-headline font-black text-on-surface">
                      {program?.name || t(copy.common.programFallback)}
                    </h2>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <StatusChip
                    label={isTuntas ? t(copy.results.passed) : t(copy.results.failed)}
                    variant={isTuntas ? 'success' : 'warning'}
                  />
                  <Link
                    to={`/programs/${enrollment.programId}`}
                    className="px-4 py-2 rounded-xl bg-surface-container-low hover:bg-surface-container-high text-primary font-headline font-bold text-xs flex items-center gap-1.5 transition-colors"
                  >
                    <span>Buka Materi</span>
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </div>

              {/* KPI Progress Cards Grid */}
              {(enrollment.priorLearningRecognition?.recognizedSubjects?.length ?? 0) > 0 && (
                <section className="space-y-3 rounded-2xl bg-success-container p-4 sm:p-5 text-primary">
                  <h3 className="font-headline text-base font-bold">
                    {t(copy.programDetail.priorLearningRecognized)}
                  </h3>
                  {enrollment.priorLearningRecognition?.recognizedSubjects?.map(item => (
                    <p key={item.subjectId} className="text-base leading-relaxed break-words">
                      {t(copy.programDetail.priorLearningSource)
                        .replace('{score}', String(item.score))
                        .replace('{source}', item.sourceTitle)}
                      {item.scoreIsEstimated && (
                        <span className="block text-sm">
                          {t(copy.programDetail.priorLearningEstimated)}
                        </span>
                      )}
                    </p>
                  ))}
                </section>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 sm:p-5 bg-surface-container-low/60 rounded-2xl space-y-2">
                  <div className="flex items-center justify-between text-on-surface-variant">
                    <span className="text-[10px] font-label font-bold uppercase tracking-wider">
                      Ketuntasan Materi
                    </span>
                    <BookOpen size={16} className="text-primary" />
                  </div>
                  <div className="text-2xl sm:text-3xl font-headline font-black text-primary">
                    {Math.round(enrollment.progress)}%
                  </div>
                  <ProgressBar
                    value={enrollment.progress}
                    variant={isTuntas ? 'success' : 'primary'}
                    size="sm"
                  />
                </div>

                <div className="p-4 sm:p-5 bg-surface-container-low/60 rounded-2xl space-y-2">
                  <div className="flex items-center justify-between text-on-surface-variant">
                    <span className="text-[10px] font-label font-bold uppercase tracking-wider">
                      Materi Selesai
                    </span>
                    <CheckCircle2 size={16} className="text-success" />
                  </div>
                  <div className="text-2xl sm:text-3xl font-headline font-black text-on-surface">
                    {
                      new Set([
                        ...(enrollment.completions?.map(item => item.materialId) ?? []),
                        ...(enrollment.priorLearningRecognition?.creditedMaterialIds ?? []),
                      ]).size
                    }
                  </div>
                  <span className="text-[10px] text-on-surface-variant font-medium block">
                    {(enrollment.priorLearningRecognition?.creditedMaterialIds.length ?? 0) > 0
                      ? t(copy.programDetail.completedMaterialsWithCredit)
                      : 'Total materi dipelajari'}
                  </span>
                </div>

                <div className="p-4 sm:p-5 bg-surface-container-low/60 rounded-2xl space-y-2">
                  <div className="flex items-center justify-between text-on-surface-variant">
                    <span className="text-[10px] font-label font-bold uppercase tracking-wider">
                      Evaluasi & Ujian
                    </span>
                    <Award size={16} className="text-secondary" />
                  </div>
                  <div className="text-2xl sm:text-3xl font-headline font-black text-secondary">
                    {completedEvals}/{totalEvals}
                  </div>
                  <ProgressBar value={evalPercent} variant="primary" size="sm" />
                </div>

                <div className="p-4 sm:p-5 bg-surface-container-low/60 rounded-2xl space-y-2">
                  <div className="flex items-center justify-between text-on-surface-variant">
                    <span className="text-[10px] font-label font-bold uppercase tracking-wider">
                      Lulus KKM
                    </span>
                    <Award size={16} className="text-success" />
                  </div>
                  <div className="text-2xl sm:text-3xl font-headline font-black text-success">
                    {passedEvals}/{totalEvals} ({passPercent}%)
                  </div>
                  <span className="text-[10px] text-success font-medium block">
                    KKM Standar Minimum (70)
                  </span>
                </div>
              </div>

              {/* Stage & Subject Curriculum Breakdown */}
              {program?.levels && program.levels.length > 0 && (
                <div className="space-y-4 pt-4">
                  <h3 className="text-base font-headline font-bold text-on-surface flex items-center gap-2">
                    <BookOpen size={18} className="text-primary" />
                    <span>Struktur Capaian Tahapan & Mata Pelajaran</span>
                  </h3>

                  <div className="space-y-4">
                    {program.levels.map(lvl => (
                      <div key={lvl.id} className="space-y-3">
                        <div className="px-3 py-1.5 rounded-lg bg-surface-container-low/80 inline-block text-xs font-headline font-bold text-primary">
                          Level {lvl.levelNumber ?? lvl.order}: {lvl.name}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {lvl.stages?.map(stg => (
                            <div
                              key={stg.id}
                              className="p-5 rounded-2xl bg-surface-container-low/40 border border-outline-variant/10 space-y-3"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="text-[10px] font-bold text-secondary uppercase tracking-wider block">
                                    Tahap {stg.stageCode || ''}
                                  </span>
                                  <h4 className="font-headline font-bold text-on-surface text-sm">
                                    {stg.name}
                                  </h4>
                                </div>
                                <span className="text-[10px] font-mono font-bold text-on-surface-variant">
                                  KKM: {stg.passingScore ?? 70}%
                                </span>
                              </div>

                              {/* Subjects inside stage */}
                              <div className="space-y-2 pt-2 border-t border-outline-variant/10">
                                {stg.subjects?.map(sbj => (
                                  <div
                                    key={sbj.id}
                                    className="flex items-center justify-between text-xs py-1 text-on-surface"
                                  >
                                    <div className="flex items-center gap-2 truncate">
                                      <div className="w-1.5 h-1.5 rounded-full bg-primary/60" />
                                      <span className="font-medium truncate">{sbj.name}</span>
                                    </div>
                                    <span className="text-[10px] font-mono text-on-surface-variant font-bold shrink-0">
                                      {sbj.sessions?.length || 0} Pertemuan
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Evaluations Tracker List */}
              {programEvaluations.length > 0 && (
                <div className="space-y-3 pt-6 border-t border-outline-variant/10">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-headline font-bold text-on-surface flex items-center gap-2">
                      <Award size={18} className="text-primary" />
                      <span>Daftar Evaluasi & Ujian Tahapan</span>
                    </h3>
                    <span className="text-xs font-mono font-bold text-on-surface-variant">
                      {passPercent}% Lulus KKM
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {programEvaluations.map(ev => {
                      const result = enrollment.results?.find(r => {
                        const meta = r.metadata as Record<string, unknown> | undefined;
                        const rUnknown = r as unknown as Record<string, unknown>;
                        const resultEvalId =
                          (meta?.evaluationId as string | undefined) ||
                          (rUnknown.evaluationId as string | undefined);
                        return resultEvalId === ev.id;
                      });

                      const meta = result?.metadata as Record<string, unknown> | undefined;
                      const rUnknown = result as unknown as Record<string, unknown>;
                      const evalPassed =
                        meta?.isPassed !== undefined
                          ? Boolean(meta.isPassed)
                          : Boolean(rUnknown?.isPassed);
                      const score =
                        result?.score ??
                        (meta?.score as number | undefined) ??
                        (rUnknown?.score as number | undefined);
                      const accessState = evaluationAccessById.get(ev.id);
                      const evaluationLocked = !result && (accessState?.isLocked ?? true);
                      const remainingMaterials = Math.max(
                        0,
                        (accessState?.requiredMaterialsCount ?? 0) -
                          (accessState?.completedMaterialsCount ?? 0)
                      );

                      return (
                        <div
                          key={ev.id}
                          className={`p-4 rounded-2xl border transition-all flex items-center justify-between gap-3 text-xs ${
                            result
                              ? evalPassed
                                ? 'bg-success/5 border-success/20'
                                : 'bg-amber-500/5 border-amber-500/20'
                              : evaluationLocked
                                ? 'bg-surface-container-low/60 border-outline-variant/10 opacity-70'
                                : 'bg-surface-container-low/40 border-outline-variant/10'
                          }`}
                        >
                          <div className="min-w-0">
                            <span className="text-[9px] font-bold text-secondary uppercase tracking-wider block truncate">
                              {ev.contextLabel}
                            </span>
                            <span className="font-headline font-bold text-on-surface block truncate mt-0.5">
                              {ev.title}
                            </span>
                            <span className="text-[10px] text-on-surface-variant font-medium">
                              Standar KKM: {ev.passingScore}%
                            </span>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            {result ? (
                              <div className="text-right">
                                <div className="flex items-center gap-1.5 justify-end">
                                  <span className="font-mono font-bold text-on-surface">
                                    {score !== undefined ? Math.round(Number(score)) : 0}%
                                  </span>
                                  <span
                                    className={`text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider ${
                                      evalPassed
                                        ? 'bg-success text-white'
                                        : 'bg-amber-500 text-white'
                                    }`}
                                  >
                                    {evalPassed ? 'Lulus' : 'Remidi'}
                                  </span>
                                </div>
                                <Link
                                  to={`/evaluations/${ev.id}`}
                                  className="text-[9px] font-bold text-primary hover:underline block mt-0.5"
                                >
                                  Lihat Pembahasan
                                </Link>
                              </div>
                            ) : legacyQualifiedIds.has(ev.id) ? (
                              <span className="max-w-48 rounded-xl bg-success-container p-3 text-sm font-semibold leading-relaxed text-primary">
                                {t(copy.programDetail.priorLearningRecognized)}
                              </span>
                            ) : evaluationLocked ? (
                              <div
                                className="flex max-w-40 flex-col items-end gap-1 text-right text-on-surface-variant"
                                aria-disabled="true"
                              >
                                <span className="inline-flex items-center gap-1 rounded-xl bg-surface-container-high px-3 py-1.5 text-[10px] font-headline font-bold uppercase">
                                  <Lock size={12} />
                                  {t(copy.results.evaluationLocked)}
                                </span>
                                <span className="text-[9px] font-medium leading-tight">
                                  {remainingMaterials > 0
                                    ? t(copy.results.completeRemainingMaterials).replace(
                                        '{count}',
                                        String(remainingMaterials)
                                      )
                                    : t(copy.results.completeMaterialsFirst)}
                                </span>
                              </div>
                            ) : (
                              <Link
                                to={`/evaluations/${ev.id}`}
                                className="px-3.5 py-1.5 rounded-xl bg-secondary text-on-secondary text-[10px] font-headline font-bold uppercase shadow-sm hover:opacity-90 transition-opacity"
                              >
                                Mulai Ujian
                              </Link>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {reviewableResults.length > 0 && (
                <section className="space-y-4 rounded-2xl bg-surface-container-low p-5 sm:p-6">
                  <div className="space-y-1">
                    <h3 className="font-headline text-lg font-bold text-on-surface">
                      {t(copy.results.answerHistory)}
                    </h3>
                    <p className="text-sm text-on-surface-variant">
                      {t(copy.results.answerHistoryDesc)}
                    </p>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {reviewableResults.map(result => (
                      <div
                        key={result.id}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-surface-container-lowest p-4"
                      >
                        <div className="min-w-0">
                          <p className="m-0 break-words text-sm font-bold text-on-surface">
                            {result.title}
                          </p>
                          {result.score !== null && result.score !== undefined && (
                            <p className="m-0 text-xs text-on-surface-variant">
                              {t(copy.results.score)}: {Math.round(result.score)}%
                            </p>
                          )}
                        </div>
                        {result.evaluationId && result.attemptId ? (
                          <Link
                            to={`/evaluations/${result.evaluationId}?attemptId=${encodeURIComponent(result.attemptId)}`}
                            className="inline-flex min-h-11 items-center rounded-xl bg-primary px-4 py-2 text-xs font-bold text-on-primary no-underline hover:bg-primary-container"
                          >
                            {t(copy.results.reviewScore)}
                          </Link>
                        ) : (
                          <p className="m-0 text-xs text-on-surface-variant">
                            {t(copy.results.answersUnavailable)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          );
        })()}

      {/* Overall Summary Stats */}
      <div className="bg-surface-container-lowest rounded-3xl shadow-level1 p-6 md:p-8 border border-outline-variant/10">
        <h3 className="text-lg font-headline font-bold text-on-surface mb-6">
          {t(copy.results.overallSummary)}
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-surface-container-low rounded-2xl">
            <p className="text-xs font-label text-on-surface-variant uppercase tracking-wide mb-1">
              {t(copy.results.totalPrograms)}
            </p>
            <p className="text-2xl sm:text-3xl font-headline font-black text-on-surface">
              {enrollments.length}
            </p>
          </div>

          <div className="p-4 bg-surface-container-low rounded-2xl">
            <p className="text-xs font-label text-on-surface-variant uppercase tracking-wide mb-1">
              {t(copy.results.completed)}
            </p>
            <p className="text-2xl sm:text-3xl font-headline font-black text-success">
              {completedCount}
            </p>
          </div>

          <div className="p-4 bg-surface-container-low rounded-2xl">
            <p className="text-xs font-label text-on-surface-variant uppercase tracking-wide mb-1">
              {t(copy.results.inProgress)}
            </p>
            <p className="text-2xl sm:text-3xl font-headline font-black text-amber-600">
              {enrollments.length - completedCount}
            </p>
          </div>

          <div className="p-4 bg-surface-container-low rounded-2xl">
            <p className="text-xs font-label text-on-surface-variant uppercase tracking-wide mb-1">
              {t(copy.results.avgProgress)}
            </p>
            <p className="text-2xl sm:text-3xl font-headline font-black text-primary">
              {avgCompletion}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
